import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  notification,
  theme,
  Row,
  Col,
  List,
  Typography,
  Button,
  Spin,
  Empty,
  Input,
  Form,
  Modal,
  Upload,
  message
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { globalOp } from 'store/ducks/Global';
import { RootState } from 'store/configureStore';
import { ThemeList, TitleListEN } from 'store/ducks/globalTypes';
import { useDispatch, useSelector } from 'react-redux';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import { RcFile } from 'antd/es/upload';
import { ICompany } from 'store/ducks/Global/types';
import TitleBar from 'components/TitleBar/TitleBar';
import signUpSuperUsersFormItems from 'constants/signUpSuperUsers';
import globalCompanies from 'constants/globalCompanies';
import CustomTitle from 'components/CustomTitle';
import ApiService from 'utils/ApiCalls';
import CustomSelect from 'components/CustomSelect';
import * as Styled from './SuperAdmin.styled';

const { useToken } = theme;

export const SuperAdmin: React.FC = () => {
  const { confirm } = Modal;
  const navigate = useNavigate();
  const { scheme } = useSelector((state: RootState) => state.global);
  const { token } = useToken();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [formCompany] = Form.useForm();
  const { Text } = Typography;
  const [globalUsers, setGlobalUsers] = useState<any[]>([]);
  const [globalCompanyData, setGlobalCompanyData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTermCompanies, setSearchTermCompanies] = useState('');
  const [userForms, setUserForms] = useState([0]);
  const [globalCompanyForms, setGlobalCompanyForms] = useState([1]);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [visibleCols, setVisibleCols] = useState<number[]>([1, 2]);
  const isUsersVisible = visibleCols[0] === 1;
  const [formData, setFormData] = useState<Record<number, Record<string, any>>>(
    {}
  );
  const [formDataCompany, setFormDataCompany] = useState<
    Record<number, Record<string, any>>
  >({});
  const [uploading, setUploading] = useState(false);
  const [fileExcelAccessControl, setFileExcelAccessControl] =
    useState<File | null>(null);
  const dispatch = useDispatch();
  useEffect(() => {
    const urlToken = searchParams.get('token');

    if (!urlToken) return;

    const verifyLogin = async () => {
      try {
        const { data } = await ApiService.verifySuperAdminLogin(urlToken);

        const verifiedToken = data.token;
        const roles = data.user.role;

        sessionStorage.setItem(
          'user',
          JSON.stringify({
            token: verifiedToken,
            role: roles
          })
        );

        dispatch(globalOp.setSuperAdmin(true));

        notification.success({
          message: 'Info',
          description: data.message,
          duration: 20
        });

        navigate('/super_admin', { replace: true });
      } catch (error) {
        sessionStorage.removeItem('user');
        notification.error({
          message: 'Info',
          description: 'Invalid or expired login link.'
        });
        navigate('/', { replace: true });
      }
    };

    verifyLogin();
  }, [searchParams, navigate, dispatch]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [users, companies] = await Promise.all([
        globalOp.fetchGlobalUsers(),
        globalOp.fetchGlobalCompanies()
      ]);
      if (users) setGlobalUsers(users);
      if (companies) setGlobalCompanyData(companies);
    } catch (error) {
      console.log('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  const urlToken = searchParams.get('token');
  useEffect(() => {
    fetchAll();
  }, [urlToken]);

  const term = (searchTerm ?? '').toLowerCase();

  const filteredUsers = (globalUsers ?? [])
    .filter(
      (user) =>
        typeof user.email === 'string' &&
        user.email.toLowerCase().includes(term)
    )
    .sort((a, b) => a.email.localeCompare(b.email));

  const filteredCompanies = globalCompanyData
    .filter((company) =>
      company.company_name
        .toLowerCase()
        .includes(searchTermCompanies.toLowerCase())
    )
    .sort((a, b) => a.company_name.localeCompare(b.company_name));

  const handleSubmit = async () => {
    const usersData = userForms.map((formIndex) => {
      const userFormData = formData[formIndex];
      if (!userFormData) {
        return {};
      }
      return {
        password: userFormData.password,
        email: userFormData.email,
        deactivation_date: userFormData.deactivation_date || null
      };
    });
    try {
      const response = await globalOp.signupSuperUser(usersData);

      if (response?.results?.[0]?.status === 'success') {
        notification.success({
          message: 'Info',
          description: 'All users have been successfully created.',
          duration: 20
        });
        setFormData({});
        form.resetFields();
        form.setFieldsValue(
          Object.fromEntries(
            userForms.map((index) => [
              index,
              Object.fromEntries(
                signUpSuperUsersFormItems.map((item) => [item.name, undefined])
              )
            ])
          )
        );
      } else {
        notification.error({
          message: 'Signup Failed',
          description: 'One or more users could not be created.',
          duration: 10
        });
      }
    } catch (error: any) {
      console.warn('Caught in handleSubmit:', error.message);
    }
  };

  const handleFormChange = (changedValues: any, allValues: any) => {
    const formIndex = Object.keys(changedValues)[0];
    if (formIndex) {
      setFormData({
        ...formData,
        [formIndex]: allValues[formIndex]
      });
    }
  };

  const handleFormChangeCompany = (changedValues: any, allValues: any) => {
    const formIndex = Object.keys(changedValues)[0];
    if (formIndex) {
      setFormDataCompany({
        ...formDataCompany,
        [formIndex]: allValues[formIndex]
      });
    }
  };

  const handleSoftDelete = (email: string, schema_id: string) => {
    confirm({
      title: 'Confirm Inactivation',
      content:
        'Once deleted, this record cannot be restored and may affect dependent records. Would you like to continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await globalOp.softDeleteUserBySuperAdmin(
            email,
            schema_id
          );
          notification.success({
            message: 'Info',
            description:
              response?.data?.message || 'User soft deleted successfully'
          });

          fetchAll();
        } catch (error: any) {
          notification.error({
            message: 'Error',
            description: error?.message || 'Soft delete failed'
          });
        }
      },
      onCancel: () => {}
    });
  };

  const handleSubmitGlobalCompanies = async () => {
    const formIndex = globalCompanyForms[0];
    const companyFormData = formDataCompany[formIndex];

    if (!companyFormData) {
      notification.warning({
        message: '',
        description: 'Please fill out the company form.'
      });
      return;
    }

    const companyData = {
      global_company_id: generateUniqueId(),
      local_company_id: generateUniqueId(),
      schema_id: companyFormData.schema_id,
      company_name: companyFormData.company_name,
      access_status: companyFormData.access_status,
      billing_status: companyFormData.billing_status,
      emergency_status: companyFormData.emergency_status,
      description: companyFormData.description
    };

    try {
      const response = await globalOp.postGlobalCompanies(companyData);
      notification.success({
        message: 'Info',
        description: response.message,
        duration: 20
      });
      form.resetFields();
      form.setFieldsValue(
        Object.fromEntries(
          userForms.map((index) => [
            index,
            Object.fromEntries(
              signUpSuperUsersFormItems.map((item) => [item.name, undefined])
            )
          ])
        )
      );
    } catch (error: any) {
      console.warn('Caught in handleSubmit:', error.message);
    }
  };

  const handleSubmitGlobalCompanyUpdate = async () => {
    if (!selectedCompany) {
      notification.warning({
        message: '',
        description: 'Please fill out the company form.'
      });
      return;
    }

    const companyData = {
      schema_id: selectedCompany.schema_id,
      company_name: selectedCompany.company_name,
      access_status: selectedCompany.access_status,
      billing_status: selectedCompany.billing_status,
      emergency_status: selectedCompany.emergency_status,
      description: selectedCompany.description,
      global_company_id: selectedCompany.global_company_id,
      local_company_id: selectedCompany.local_company_id
    };

    try {
      const response = await globalOp.postGlobalCompanies(companyData);
      notification.success({
        message: 'Info',
        description: response.message,
        duration: 20
      });
      form.resetFields();
      form.setFieldsValue(
        Object.fromEntries(
          userForms.map((index) => [
            index,
            Object.fromEntries(
              signUpSuperUsersFormItems.map((item) => [item.name, undefined])
            )
          ])
        )
      );
    } catch (error: any) {
      console.warn('Caught in handleSubmit:', error.message);
    }
  };

  const beforeUploadExcelAccessControl = (info: RcFile) => {
    setFileExcelAccessControl(info);
    return false;
  };

  const onFinish = async (values: any) => {
    try {
      if (!values.schema_id) {
        message.error('Schema ID is required.');
        return;
      }

      const { schema_id } = values;

      if (!fileExcelAccessControl) {
        message.error('Please upload at least one file.');
        return;
      }

      setUploading(true);

      const results = [];
      if (fileExcelAccessControl) {
        const excelAccessControl = await globalOp.uploadExcelAccessControl(
          schema_id,
          fileExcelAccessControl
        );
        results.push(excelAccessControl);
      }

      setFileExcelAccessControl(null);

      form.resetFields();
    } catch (error) {
      console.log(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Styled.StyledContainer theme={scheme as unknown as ThemeList}>
      <>
        <TitleBar title={TitleListEN.SUPER_ADMIN} />
        <Styled.MainTabs>
          <Styled.StyledButton
            token={token}
            type={isUsersVisible ? 'primary' : 'default'}
            onClick={() => setVisibleCols([1, 2])}
          >
            Users
          </Styled.StyledButton>
          <Styled.StyledButton
            token={token}
            type={isUsersVisible ? 'primary' : 'default'}
            onClick={() => setVisibleCols([3, 4])}
          >
            Companies
          </Styled.StyledButton>
          <Styled.StyledButton
            token={token}
            type={isUsersVisible ? 'primary' : 'default'}
            onClick={() => setVisibleCols([5])}
          >
            Upload files
          </Styled.StyledButton>
        </Styled.MainTabs>
        <Row justify='center'>
          {visibleCols.includes(1) && (
            <Col span={8}>
              <Styled.FormContainer token={token}>
                <CustomTitle title='Global Users' level={3} />
                <Styled.ViewUsersContainer>
                  {loading ? (
                    <Spin tip='Loading users...' />
                  ) : filteredUsers.length === 0 ? (
                    <Empty description='No users found' />
                  ) : (
                    <>
                      <Input.Search
                        placeholder='Search by email'
                        allowClear
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <List
                        itemLayout='horizontal'
                        dataSource={filteredUsers}
                        bordered
                        renderItem={(user) => (
                          <List.Item
                            key={user.email}
                            actions={[
                              !user.deleted && (
                                <Button
                                  key='soft-delete'
                                  type='primary'
                                  danger
                                  onClick={() =>
                                    handleSoftDelete(user.email, user.schema_id)
                                  }
                                >
                                  Soft Delete
                                </Button>
                              )
                            ]}
                          >
                            <List.Item.Meta
                              title={<Text strong>{user.email}</Text>}
                              description={
                                <div>
                                  <Text type='secondary'>
                                    Schema_id: {user.schema_id}
                                  </Text>
                                  {user.deleted ? 'Deleted user' : ''}
                                </div>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </>
                  )}
                </Styled.ViewUsersContainer>
              </Styled.FormContainer>
            </Col>
          )}
          {visibleCols.includes(2) && (
            <Col span={8}>
              <Styled.FormContainer token={token}>
                <CustomTitle title='Sign up super user' level={3} />
                <Styled.ViewUsersContainer>
                  <Form
                    form={form}
                    name='userForm'
                    layout='vertical'
                    onFinish={handleSubmit}
                    autoComplete='off'
                    onValuesChange={handleFormChange}
                  >
                    {userForms.map((formIndex, index) => (
                      <Styled.InputForm key={`globalCompany_${formIndex}`}>
                        {signUpSuperUsersFormItems.map((item, itemIndex) => (
                          <Styled.StyledFormItem
                            token={token}
                            key={`superUser_${formIndex}_${
                              item.name
                            }_${itemIndex}_${generateUniqueId()}`}
                            label={item.label}
                            name={[formIndex, item.name]}
                            rules={item.rules}
                            initialValue={formData[formIndex]?.[item.name]}
                          >
                            {item.type === 'custom' && item.render ? (
                              item.render()
                            ) : (
                              <Input type={item.type} />
                            )}
                          </Styled.StyledFormItem>
                        ))}
                      </Styled.InputForm>
                    ))}
                    <Row>
                      <Col>
                        <Styled.StyledButton
                          token={token}
                          type='primary'
                          size='large'
                          htmlType='submit'
                        >
                          Save
                        </Styled.StyledButton>
                      </Col>
                    </Row>
                  </Form>
                </Styled.ViewUsersContainer>
              </Styled.FormContainer>
            </Col>
          )}
          {visibleCols.includes(3) && (
            <Col span={8}>
              <Styled.FormContainer token={token}>
                <CustomTitle title='Global Companies' level={3} />
                <Styled.ViewUsersContainer>
                  {loading ? (
                    <Spin tip='Loading companies...' />
                  ) : filteredCompanies.length === 0 ? (
                    <Empty description='No company found' />
                  ) : (
                    <>
                      <CustomSelect
                        options={filteredCompanies.map((company) => ({
                          label: company.company_name,
                          value: company.schema_id,
                          fullData: company
                        }))}
                        value={selectedCompany?.schema_id}
                        onChange={(value: string, option: string) => {
                          const selected = filteredCompanies.find(
                            (c) => c.schema_id === value
                          );
                          setSelectedCompany({ ...selected });
                        }}
                        filterOption={(
                          input: string,
                          option: { label: string }
                        ) =>
                          option?.label
                            ?.toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />

                      {selectedCompany && (
                        <Styled.SelectedCompany>
                          <Input
                            value={selectedCompany.company_name}
                            onChange={(e) =>
                              setSelectedCompany({
                                ...selectedCompany,
                                company_name: e.target.value
                              })
                            }
                            placeholder='Company Name'
                          />
                          <Input
                            type='number'
                            value={selectedCompany.global_company_id ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedCompany({
                                ...selectedCompany,
                                global_company_id:
                                  value === '' ? undefined : Number(value)
                              });
                            }}
                            disabled
                            placeholder='Global Company ID'
                          />
                          <Input
                            type='number'
                            value={selectedCompany.local_company_id ?? ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setSelectedCompany({
                                ...selectedCompany,
                                local_company_id:
                                  value === '' ? undefined : Number(value)
                              });
                            }}
                            disabled
                            placeholder='Local Company ID'
                          />
                          <Input
                            value={selectedCompany.schema_id}
                            onChange={(e) =>
                              setSelectedCompany({
                                ...selectedCompany,
                                schema_id: e.target.value
                              })
                            }
                            placeholder='Schema ID'
                          />
                          <Input
                            value={selectedCompany.access_status}
                            onChange={(e) =>
                              setSelectedCompany({
                                ...selectedCompany,
                                access_status: e.target.value
                              })
                            }
                            placeholder='Access Status'
                          />
                          <Input
                            value={selectedCompany.billing_status}
                            onChange={(e) =>
                              setSelectedCompany({
                                ...selectedCompany,
                                billing_status: e.target.value
                              })
                            }
                            placeholder='Billing Status'
                          />
                          <Input
                            value={selectedCompany.description}
                            onChange={(e) =>
                              setSelectedCompany({
                                ...selectedCompany,
                                description: e.target.value
                              })
                            }
                            placeholder='Description'
                          />
                          <Input
                            value={selectedCompany.emergency_status}
                            onChange={(e) =>
                              setSelectedCompany({
                                ...selectedCompany,
                                emergency_status: e.target.value
                              })
                            }
                            placeholder='Emergency Status'
                          />

                          <Button
                            type='primary'
                            onClick={() => handleSubmitGlobalCompanyUpdate()}
                          >
                            Save
                          </Button>
                        </Styled.SelectedCompany>
                      )}
                    </>
                  )}
                </Styled.ViewUsersContainer>
              </Styled.FormContainer>
            </Col>
          )}
          {visibleCols.includes(4) && (
            <Col span={8}>
              <Styled.FormContainer token={token}>
                <CustomTitle title='Sign up company' level={3} />
                <Styled.ViewUsersContainer>
                  <Form
                    form={formCompany}
                    name='globalCompanies'
                    layout='vertical'
                    onFinish={handleSubmitGlobalCompanies}
                    autoComplete='off'
                    onValuesChange={handleFormChangeCompany}
                  >
                    {globalCompanyForms.map((formIndex, index) => (
                      <Styled.InputForm key={`globalCompany_${formIndex}`}>
                        {globalCompanies.map((item, itemIndex) => (
                          <Styled.StyledFormItem
                            token={token}
                            key={`globalCompany_${
                              item.name
                            }_${itemIndex}_${formIndex}_${generateUniqueId()}`}
                            label={item.label}
                            name={[formIndex, item.name]}
                            rules={item.rules}
                            initialValue={
                              formDataCompany[formIndex]?.[item.name]
                            }
                          >
                            {item.type === 'custom' && item.render ? (
                              item.render()
                            ) : (
                              <Input type={item.type} />
                            )}
                          </Styled.StyledFormItem>
                        ))}
                      </Styled.InputForm>
                    ))}
                    <Row>
                      <Col>
                        <Styled.StyledButton
                          token={token}
                          type='primary'
                          size='large'
                          htmlType='submit'
                        >
                          Save
                        </Styled.StyledButton>
                      </Col>
                    </Row>
                  </Form>
                </Styled.ViewUsersContainer>
              </Styled.FormContainer>
            </Col>
          )}
          {visibleCols.includes(5) && (
            <Col span={8}>
              <Styled.FormContainer token={token}>
                <CustomTitle title='Upload files' level={3} />
                <Form
                  form={form}
                  layout='vertical'
                  onFinish={onFinish}
                  className='max-w-md mx-auto'
                >
                  <Form.Item
                    name='schema_id'
                    label='Schema ID'
                    rules={[
                      { required: true, message: 'Please enter the schema ID' }
                    ]}
                  >
                    <Input placeholder='Enter schema ID' />
                  </Form.Item>
                  <Form.Item label='Upload Excel for Access Control'>
                    <Upload
                      beforeUpload={beforeUploadExcelAccessControl}
                      fileList={
                        fileExcelAccessControl
                          ? [fileExcelAccessControl as any]
                          : []
                      }
                      onRemove={() => setFileExcelAccessControl(null)}
                      maxCount={1}
                    >
                      <Button icon={<UploadOutlined />}>
                        Select excel file for Access Control
                      </Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type='primary'
                      htmlType='submit'
                      loading={uploading}
                    >
                      {uploading ? 'Uploading…' : 'Upload file(s)'}
                    </Button>
                  </Form.Item>
                </Form>
              </Styled.FormContainer>
            </Col>
          )}
        </Row>
      </>
    </Styled.StyledContainer>
  );
};

export default SuperAdmin;
